// src/components/AgentFloatingChat.jsx

import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  RefreshCw,
  Send,
  Sparkles,
  X,
} from "lucide-react";

import { sendAgentMessage } from "../services/agentServices";
import AgentMemorySuggestion from "./AgentMemorySuggestion";

import PsyaLauncher from "../assets/psya/psya-launcher.gif";
import PsyaAutoridade from "../assets/psya/mp4-autoridade.gif";
import PsyaPensando from "../assets/psya/mmp4-pensando.gif";
import PsyaPesquisando from "../assets/psya/mp4-pesquisando.gif";
import PsyaIdeia from "../assets/psya/mp4-ideia.gif";
import PsyaFallback from "../assets/psya/mp4-autoridade.gif";

import "../sass/components/agentFloatingChat.scss";

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_NAME: "psy_usuario",
};

const QUICK_QUESTIONS = [
  "O que devo priorizar hoje?",
  "Quem devo cobrar primeiro?",
  "Como está minha receita?",
  "Tenho pacientes sem retorno?",
  "Como está minha agenda amanhã?",
];

const PSYA_STATUS = {
  AUTHORITY: "authority",
  THINKING: "thinking",
  RESEARCHING: "researching",
  INSIGHT: "insight",
};

const PSYA_AVATARS = {
  [PSYA_STATUS.AUTHORITY]: PsyaAutoridade,
  [PSYA_STATUS.THINKING]: PsyaPensando,
  [PSYA_STATUS.RESEARCHING]: PsyaPesquisando,
  [PSYA_STATUS.INSIGHT]: PsyaIdeia,
};

const PSYA_STATUS_LABELS = {
  [PSYA_STATUS.AUTHORITY]: "Pronta para ajudar",
  [PSYA_STATUS.THINKING]: "Pensando na melhor resposta",
  [PSYA_STATUS.RESEARCHING]: "Consultando sua operação",
  [PSYA_STATUS.INSIGHT]: "Informação encontrada",
};

function readAuth(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();
  return clean ? clean.split(/\s+/)[0] : "";
}

function createMessage(role, content, extra = {}) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    content,
    ...extra,
  };
}

function getErrorMessage(error) {
  return (
    error?.data?.message ||
    error?.message ||
    "Não foi possível conversar com a PSYA."
  );
}

function getSuggestionId(suggestion) {
  return (
    suggestion?.existingMemoryId ||
    suggestion?.id ||
    `${suggestion?.categoria || "memoria"}-${suggestion?.chave || "sem-chave"}`
  );
}

function getMessageAvatar(status) {
  return PSYA_AVATARS[status] || PSYA_AVATARS[PSYA_STATUS.AUTHORITY];
}

function renderInline(text, keyPrefix = "inline") {
  const source = String(text || "");
  const parts = source.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;

    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }

    return <Fragment key={key}>{part}</Fragment>;
  });
}

function FormattedMessage({ content }) {
  const lines = String(content || "").split("\n");

  return (
    <div className="psy-agent__formatted-message">
      {lines.map((rawLine, index) => {
        const line = rawLine.trimEnd();
        const key = `line-${index}`;

        if (!line.trim()) {
          return <span key={key} className="psy-agent__message-space" />;
        }

        const bullet = line.match(/^\s*[-•]\s+(.+)$/);
        if (bullet) {
          return (
            <div key={key} className="psy-agent__message-list-item">
              <span aria-hidden="true">•</span>
              <p>{renderInline(bullet[1], key)}</p>
            </div>
          );
        }

        const numbered = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
        if (numbered) {
          return (
            <div key={key} className="psy-agent__message-list-item psy-agent__message-list-item--numbered">
              <span>{numbered[1]}.</span>
              <p>{renderInline(numbered[2], key)}</p>
            </div>
          );
        }

        return <p key={key}>{renderInline(line, key)}</p>;
      })}
    </div>
  );
}

export default function AgentFloatingChat({
  enabled = true,
  days = 30,
  timezone = "America/Sao_Paulo",
}) {
  const navigate = useNavigate();

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const researchTimerRef = useRef(null);
  const insightTimerRef = useRef(null);
  const focusTimerRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [suggestedActions, setSuggestedActions] = useState([]);
  const [memorySuggestions, setMemorySuggestions] = useState([]);
  const [psyaStatus, setPsyaStatus] = useState(PSYA_STATUS.AUTHORITY);

  const userName = useMemo(
    () => getFirstName(readAuth(AUTH_KEYS.USER_NAME)),
    []
  );

  const [messages, setMessages] = useState(() => [
    createMessage(
      "assistant",
      `Olá${userName ? `, ${userName}` : ""}! Sou a PSYA. Posso consultar sua agenda, financeiro, pacientes, sessões, leads e prioridades — e também executar algumas ações com sua confirmação.`,
      { avatarStatus: PSYA_STATUS.AUTHORITY }
    ),
  ]);

  const history = useMemo(
    () =>
      messages
        .filter((item) => item.role === "user" || item.role === "assistant")
        .slice(-10)
        .map((item) => ({
          role: item.role,
          content: item.content,
        })),
    [messages]
  );

  const currentAvatar = useMemo(
    () => PSYA_AVATARS[psyaStatus] || PsyaFallback,
    [psyaStatus]
  );

  const clearPsyaTimers = useCallback(() => {
    [researchTimerRef, insightTimerRef, focusTimerRef].forEach((ref) => {
      if (ref.current) {
        window.clearTimeout(ref.current);
        ref.current = null;
      }
    });
  }, []);

  const scheduleAuthorityReturn = useCallback((delay = 4500) => {
    if (insightTimerRef.current) {
      window.clearTimeout(insightTimerRef.current);
    }

    insightTimerRef.current = window.setTimeout(() => {
      setPsyaStatus(PSYA_STATUS.AUTHORITY);
      insightTimerRef.current = null;
    }, delay);
  }, []);

  useEffect(() => () => clearPsyaTimers(), [clearPsyaTimers]);

  useEffect(() => {
    if (!open) return;

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, memorySuggestions, open]);

  useEffect(() => {
    if (open && !loading) {
      focusTimerRef.current = window.setTimeout(
        () => inputRef.current?.focus(),
        120
      );
    }

    return () => {
      if (focusTimerRef.current) {
        window.clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
    };
  }, [open, loading]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const sendMessage = useCallback(
    async (value = message) => {
      const text = String(value || "").trim();

      if (!text || loading || !enabled) return;

      const token = readAuth(AUTH_KEYS.TOKEN);

      if (!token) {
        setError("Sua sessão expirou. Faça login novamente.");
        return;
      }

      clearPsyaTimers();

      const userMessage = createMessage("user", text);
      setMessages((current) => [...current, userMessage]);
      setMessage("");
      setError("");
      setLoading(true);
      setSuggestedActions([]);
      setMemorySuggestions([]);
      setPsyaStatus(PSYA_STATUS.THINKING);

      researchTimerRef.current = window.setTimeout(() => {
        setPsyaStatus(PSYA_STATUS.RESEARCHING);
        researchTimerRef.current = null;
      }, 1100);

      try {
        const result = await sendAgentMessage({
          token,
          message: text,
          history,
          days,
          tz: timezone,
        });

        if (researchTimerRef.current) {
          window.clearTimeout(researchTimerRef.current);
          researchTimerRef.current = null;
        }

        const nextActions = Array.isArray(result?.suggestedActions)
          ? result.suggestedActions.slice(0, 4)
          : [];

        const nextMemories = Array.isArray(result?.memorySuggestions)
          ? result.memorySuggestions
          : [];

        const hasInsight =
          nextMemories.length > 0 ||
          nextActions.length > 0 ||
          Boolean(result?.intelligence?.nextBestAction);

        const answer =
          result?.answer || "Não consegui gerar uma resposta neste momento.";

        const answerAvatarStatus = hasInsight
          ? PSYA_STATUS.INSIGHT
          : PSYA_STATUS.AUTHORITY;

        setMessages((current) => [
          ...current,
          createMessage("assistant", answer, {
            avatarStatus: answerAvatarStatus,
          }),
        ]);

        setSuggestedActions(nextActions);
        setMemorySuggestions(nextMemories);
        setPsyaStatus(answerAvatarStatus);

        if (hasInsight) scheduleAuthorityReturn();
      } catch (requestError) {
        if (researchTimerRef.current) {
          window.clearTimeout(researchTimerRef.current);
          researchTimerRef.current = null;
        }

        const errorMessage = getErrorMessage(requestError);
        setError(errorMessage);
        setPsyaStatus(PSYA_STATUS.AUTHORITY);

        setMessages((current) => [
          ...current,
          createMessage(
            "assistant",
            "Não consegui concluir essa análise agora. Tente novamente em alguns instantes.",
            {
              isError: true,
              avatarStatus: PSYA_STATUS.AUTHORITY,
            }
          ),
        ]);
      } finally {
        setLoading(false);
      }
    },
    [
      clearPsyaTimers,
      days,
      enabled,
      history,
      loading,
      message,
      scheduleAuthorityReturn,
      timezone,
    ]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      sendMessage();
    },
    [sendMessage]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleAction = useCallback(
    (action) => {
      if (action?.route) {
        navigate(action.route);
        setOpen(false);
      }
    },
    [navigate]
  );

  const handleMemoryResolved = useCallback(({ suggestion: resolvedSuggestion }) => {
    const resolvedId = getSuggestionId(resolvedSuggestion);

    setMemorySuggestions((current) =>
      current.filter((item) => getSuggestionId(item) !== resolvedId)
    );

    setPsyaStatus(PSYA_STATUS.AUTHORITY);
  }, []);

  const clearConversation = useCallback(() => {
    clearPsyaTimers();

    setMessages([
      createMessage(
        "assistant",
        `Conversa reiniciada. Como posso ajudar${userName ? `, ${userName}` : ""}?`,
        { avatarStatus: PSYA_STATUS.AUTHORITY }
      ),
    ]);

    setSuggestedActions([]);
    setMemorySuggestions([]);
    setError("");
    setMessage("");
    setPsyaStatus(PSYA_STATUS.AUTHORITY);
  }, [clearPsyaTimers, userName]);

  const handleImageError = useCallback((event) => {
    if (event.currentTarget.src !== PsyaFallback) {
      event.currentTarget.src = PsyaFallback;
    }
  }, []);

  if (!enabled) return null;

  return (
    <div className={`psy-agent ${open ? "psy-agent--open" : ""}`}>
      {open ? (
        <button
          type="button"
          className="psy-agent__backdrop"
          onClick={() => setOpen(false)}
          aria-label="Fechar chat da PSYA"
        />
      ) : null}

      {open ? (
        <section className="psy-agent__panel" aria-label="Chat da PSYA">
          <header className="psy-agent__header">
            <div className="psy-agent__identity">
              <span className={`psy-agent__avatar psy-agent__avatar--${psyaStatus}`}>
                <img
                  src={currentAvatar}
                  alt=""
                  aria-hidden="true"
                  onError={handleImageError}
                />
              </span>

              <div className="psy-agent__identity-copy">
                <span className="psy-agent__identity-kicker">
                  <Sparkles size={13} /> Assistente PsyAssist
                </span>
                <strong>PSYA</strong>
                <small>{PSYA_STATUS_LABELS[psyaStatus]}</small>
              </div>
            </div>

            <div className="psy-agent__header-actions">
              <button
                type="button"
                className="psy-agent__icon-btn"
                onClick={clearConversation}
                title="Limpar conversa"
                aria-label="Limpar conversa"
              >
                <RefreshCw size={17} />
              </button>

              <button
                type="button"
                className="psy-agent__icon-btn"
                onClick={() => setOpen(false)}
                title="Fechar"
                aria-label="Fechar chat"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          <div className="psy-agent__messages">
            {messages.map((item) => (
              <div
                key={item.id}
                className={`psy-agent__message psy-agent__message--${item.role} ${
                  item.isError ? "psy-agent__message--error" : ""
                }`}
              >
                {item.role === "assistant" ? (
                  <span className="psy-agent__message-avatar">
                    <img
                      src={getMessageAvatar(item.avatarStatus)}
                      alt=""
                      aria-hidden="true"
                      onError={handleImageError}
                    />
                  </span>
                ) : null}

                <div className="psy-agent__bubble">
                  <FormattedMessage content={item.content} />
                </div>
              </div>
            ))}

            {loading ? (
              <div className="psy-agent__message psy-agent__message--assistant">
                <span className="psy-agent__message-avatar psy-agent__message-avatar--loading">
                  <img
                    src={currentAvatar}
                    alt=""
                    aria-hidden="true"
                    onError={handleImageError}
                  />
                </span>

                <div className="psy-agent__bubble psy-agent__typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : null}

            {memorySuggestions.length ? (
              <div className="psy-agent__memory-suggestions">
                {memorySuggestions.map((suggestion) => (
                  <AgentMemorySuggestion
                    key={getSuggestionId(suggestion)}
                    token={readAuth(AUTH_KEYS.TOKEN)}
                    suggestion={suggestion}
                    onResolved={handleMemoryResolved}
                  />
                ))}
              </div>
            ) : null}

            <div ref={messagesEndRef} />
          </div>

          {suggestedActions.length ? (
            <div className="psy-agent__actions">
              <div className="psy-agent__section-label">
                <Sparkles size={13} />
                <span>Ações sugeridas</span>
              </div>

              <div className="psy-agent__actions-list">
                {suggestedActions.map((action) => (
                  <button
                    type="button"
                    key={action.key || action.route || action.label}
                    onClick={() => handleAction(action)}
                    disabled={!action.route}
                  >
                    <span>
                      <strong>{action.title || action.label}</strong>
                      {action.title && action.label ? <small>{action.label}</small> : null}
                    </span>
                    <ArrowRight size={16} />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {messages.length <= 1 && !loading ? (
            <div className="psy-agent__quick">
              {QUICK_QUESTIONS.map((question) => (
                <button
                  type="button"
                  key={question}
                  onClick={() => sendMessage(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          ) : null}

          {error ? <div className="psy-agent__error">{error}</div> : null}

          <form className="psy-agent__composer" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={2000}
              rows={1}
              placeholder="Pergunte ou peça uma ação para a PSYA..."
              disabled={loading}
            />

            <button
              type="submit"
              disabled={loading || !message.trim()}
              aria-label="Enviar mensagem"
            >
              <Send size={18} />
            </button>
          </form>

          <footer className="psy-agent__footer">
            Apoio operacional e estratégico. Decisões clínicas continuam sob responsabilidade profissional.
          </footer>
        </section>
      ) : null}

      {!open ? (
        <button
          type="button"
          className="psy-agent__launcher"
          onClick={() => setOpen(true)}
          aria-expanded={false}
          aria-label="Falar com a PSYA"
        >
          <span className="psy-agent__launcher-avatar">
            <img
              src={PsyaLauncher}
              alt=""
              aria-hidden="true"
              onError={handleImageError}
            />
            <span className="psy-agent__status" />
          </span>

          <span className="psy-agent__launcher-copy">
            <strong>Fale com a PSYA</strong>
            <small>Sua assistente inteligente</small>
          </span>
        </button>
      ) : null}
    </div>
  );
}
